import React, { useMemo, useCallback } from 'react';
import { Alert, Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import '../../assets/css/componentStyles/ConfirmNumberForm.css';

function ConfirmNumberForm({
  vehicleData,
  onAutofill,
  className = '',
  title = "We've found your vehicle:",
  question = 'Do you want us to fill in the details automatically?',
  noButtonText = 'NO, FILL MANUALLY',
  yesButtonText = 'YES, FILL IN',
  alertColor = 'info',
}) {
  // Memoized event handlers to prevent unnecessary re-renders
  const handleNoAutofill = useCallback(() => {
    onAutofill(false);
  }, [onAutofill]);

  const handleYesAutofill = useCallback(() => {
    onAutofill(true);
  }, [onAutofill]);

  // Memoized content for configurability
  const content = useMemo(
    () => ({
      title,
      question,
      noButtonText,
      yesButtonText,
    }),
    [title, question, noButtonText, yesButtonText],
  );

  return (
    <div className={classNames('confirm-number-form', 'mt-3', className)}>
      <Row className="mt-3">
        <Col>
          <Alert
            color={alertColor}
            className="add-number-form__notification"
            role="alert"
            aria-labelledby="confirm-vehicle-title"
            aria-describedby="confirm-vehicle-question"
          >
            <div className="notification-content">
              <p id="confirm-vehicle-title">{content.title}</p>
              <p className="vehicle-name" aria-label={`Vehicle: ${vehicleData.fullName}`}>
                {vehicleData.fullName}
              </p>
              <p id="confirm-vehicle-question">{content.question}</p>
            </div>
          </Alert>
        </Col>
      </Row>
      <Row>
        <Col className="confirmation-buttons">
          <Col md={4}>
            <CommonButton
              label={content.yesButtonText}
              handleClick={handleYesAutofill}
              className="add-number-form__action-button add-number-form__action-button--filled w-100"
              aria-describedby="confirm-vehicle-question"
            />
          </Col>
          <Col md={4}>
            <CommonButton
              label={content.noButtonText}
              handleClick={handleNoAutofill}
              variant={CommonButtonVariants.OUTLINED}
              className="add-number-form__action-button add-number-form__action-button--outlined w-100"
              aria-describedby="confirm-vehicle-question"
            />
          </Col>
        </Col>
      </Row>
    </div>
  );
}

ConfirmNumberForm.propTypes = {
  vehicleData: PropTypes.shape({
    makeName: PropTypes.string,
    modelName: PropTypes.string,
    fullName: PropTypes.string,
  }).isRequired,
  onAutofill: PropTypes.func.isRequired,
  className: PropTypes.string,
  title: PropTypes.string,
  question: PropTypes.string,
  noButtonText: PropTypes.string,
  yesButtonText: PropTypes.string,
  alertColor: PropTypes.oneOf([
    'primary',
    'secondary',
    'success',
    'danger',
    'warning',
    'info',
    'light',
    'dark',
  ]),
};

ConfirmNumberForm.defaultProps = {
  className: '',
  title: "We've found your vehicle:",
  question: 'Do you want us to fill in the details automatically?',
  noButtonText: 'NO, FILL MANUALLY',
  yesButtonText: 'YES, FILL IN',
  alertColor: 'info',
};

export default ConfirmNumberForm;
